/**
 * PebblesDisplay component
 * Renders a static pebbles icon alongside the user's total count
 * Used in page headers (landing page and session page)
 */

import { cn } from '@/lib/utils';
import pebblesIcon from '@/assets/pebbles-icon.png';

export interface PebblesDisplayProps {
  /** Total pebbles count to display */
  totalPebbles: number;
  /** Size variant: 'sm' | 'md' | 'lg' (default: 'md') */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * PebblesDisplay - Displays pebbles icon with count
 * Follows UI/UX framework specifications exactly
 */
export function PebblesDisplay({ totalPebbles, size = 'md' }: PebblesDisplayProps) {
  const count = totalPebbles ?? 0;

  const sizeClasses = {
    sm: { icon: 'w-4 h-4', text: 'text-sm', gap: 'gap-1' },
    md: { icon: 'w-5 h-5', text: 'text-base font-semibold', gap: 'gap-1.5' },
    lg: { icon: 'w-6 h-6', text: 'text-lg font-bold', gap: 'gap-2' },
  };

  const classes = sizeClasses[size];

  return (
    <div
      className={cn('inline-flex items-center', classes.gap)}
      role="status"
      aria-label={`${count} pebbles earned`}
    >
      <img src={pebblesIcon} alt="" aria-hidden="true" className={classes.icon} />
      <span className={cn('text-pebbles-700', classes.text)}>{count}</span>
    </div>
  );
}
